// Shared Test-button cooldown helper.
// Why: System1 rate-limits the /test report endpoint to ~3-5 pings/min per
// partner account. Buyers who click Test repeatedly will 429 themselves, so we
// enforce a 60s client-side cooldown per-provider and cache the last result
// in sessionStorage (survives route remounts, not full reloads).

import api from "./api";
import { toast } from "sonner";

const COOLDOWN_MS = 60_000;
const STORAGE_KEY = "rsoc.testCooldowns";

/** Read the timestamp of the last Test click for `provider`, or null. */
export const getCooldownUntil = (provider) => {
  try {
    const map = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    const until = map[provider];
    return until && until > Date.now() ? until : null;
  } catch { return null; }
};

/** Write a 60s cooldown for `provider` starting now. */
const setCooldown = (provider) => {
  try {
    const map = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    map[provider] = Date.now() + COOLDOWN_MS;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch { /* sessionStorage unavailable → ignore, no harm */ }
};

/** Test the given provider, enforcing a 60s cooldown. Caller receives the
 *  raw {ok, error, hint, detail, latency_ms} result so it can merge into its
 *  existing testResults state. */
export async function testProviderWithCooldown(provider) {
  const until = getCooldownUntil(provider);
  if (until) {
    const secs = Math.ceil((until - Date.now()) / 1000);
    toast.error(`${provider} test on cooldown — wait ${secs}s (prevents self-inflicted rate-limits)`);
    return null;
  }
  setCooldown(provider);
  try {
    const { data } = await api.post("/connections/test-key", { provider });
    if (data.ok) {
      toast.success(`${provider}: ${data.detail} (${data.latency_ms}ms)`);
    } else {
      const msg = data.hint ? `${data.error} — ${data.hint}` : (data.error || "test failed");
      toast.error(`${provider}: ${msg}`, { duration: 8000 });
    }
    return data;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Test failed");
    return { ok: false, error: e?.message || "test failed" };
  }
}
